import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarRange, Info } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useUnit } from "@/context/UnitContext";
import { Button, Card, Field, Loading, Page, PageHeader } from "@/components/kit";

const OPTIONS = [
  { value: "semanal", label: "Semanal", hint: "Segunda a domingo" },
  { value: "quinzenal", label: "Quinzenal", hint: "Dia 1 ao 15 e dia 16 ao fim do mês" },
  { value: "mensal", label: "Mensal", hint: "Mês do calendário ou a partir de um dia que você escolhe" },
];
const DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

function Option({ o, on, onPick }) {
  return (
    <button type="button" onClick={onPick} data-testid={`period-option-${o.value}`} className="flex items-center gap-3 rounded-xl p-4 text-left border transition-colors"
      style={{ borderColor: on ? "var(--brand)" : "var(--border)", background: on ? "var(--brand-soft)" : "var(--surface)" }}>
      <span className="w-5 h-5 rounded-full border-2 flex items-center justify-center" style={{ borderColor: on ? "var(--brand)" : "var(--border)" }}>{on && <span className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--brand)" }} />}</span>
      <span className="flex-1"><span className="label block">{o.label}</span><span className="caption">{o.hint}</span></span>
    </button>
  );
}

export default function ConferencePeriod() {
  const qc = useQueryClient();
  const { unit } = useUnit();
  const { data, isLoading } = useQuery({ queryKey: ["settings"], queryFn: () => api("/settings") });
  const [period, setPeriod] = useState("semanal");
  const [day, setDay] = useState(1);
  useEffect(() => { if (data) { setPeriod(data.conf_period || "semanal"); setDay(data.month_start_day || 1); } }, [data]);
  const save = useMutation({
    mutationFn: () => api("/settings/period", { method: "PUT", body: { conf_period: period, month_start_day: day } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings"] }); qc.invalidateQueries({ queryKey: ["conf-period"] }); toast.success("Período das conferências salvo"); },
    onError: (e) => toast.error(e.message),
  });
  if (isLoading) return <Loading />;
  return (
    <Page narrow>
      <PageHeader title="Período das conferências" subtitle={unit?.name} back />
      <div className="flex flex-col gap-4">
        <Card className="flex flex-col gap-3" data-testid="period-settings-card">
          <div className="label flex items-center gap-2"><CalendarRange size={16} style={{ color: "var(--brand)" }} /> Com que frequência a equipe faz a contagem?</div>
          {OPTIONS.map((o) => <Option key={o.value} o={o} on={period === o.value} onPick={() => setPeriod(o.value)} />)}
          {period === "mensal" && (
            <Field label="O mês começa no dia" hint={day === 1 ? "Dia 1 = mês do calendário (1 ao último dia)" : `Ex.: de ${day} de um mês até ${day - 1} do mês seguinte`}>
              <select className="input" value={day} onChange={(e) => setDay(parseInt(e.target.value, 10))} data-testid="month-start-day-select">
                {DAYS.map((d) => <option key={d} value={d}>{d === 1 ? "1 (mês do calendário)" : d}</option>)}
              </select>
            </Field>
          )}
        </Card>
        <div className="flex gap-3 rounded-xl p-3" style={{ background: "var(--brand-tint)" }}>
          <Info size={18} style={{ color: "var(--brand)" }} className="shrink-0 mt-0.5" />
          <p className="text-sm">Vale para as próximas conferências desta unidade. As já criadas mantêm o período original.</p>
        </div>
        <Button loading={save.isPending} onClick={() => save.mutate()} data-testid="save-period-button">Salvar</Button>
      </div>
    </Page>
  );
}
